import { z } from 'zod'

/**
 * Runtime configuration contract.
 *
 * One Spark build serves every host. Each IIS site gets its own `config.json`
 * next to `index.html` (see `deploy/config/`), fetched before the app boots.
 * Nothing host-specific may be baked in at build time.
 */
export const LANGUAGES = ['de', 'en', 'fr'] as const
export type Language = (typeof LANGUAGES)[number]

export const runtimeConfigSchema = z.object({
  /** Which deployment ring this host belongs to. Shown as a badge outside production. */
  environment: z.enum(['development', 'test', 'production']),
  /** Country instance of the Ign8 API this host talks to. */
  country: z.enum(['DE', 'CH', 'FR']),
  /** `internal` = Wesser staff (workforce tenant), `external` = fundraisers/partners (CIAM tenant). */
  audience: z.enum(['internal', 'external']),
  defaultLanguage: z.enum(LANGUAGES).default('de'),

  api: z.object({
    /** Ign8 API origin, no trailing slash. */
    baseUrl: z.url(),
    /** Delegated scopes requested for API access tokens. */
    scopes: z.array(z.string().min(1)).min(1),
  }),

  auth: z.object({
    /** SPA app registration in the tenant this host signs in against. */
    clientId: z.guid(),
    /** Full authority URL incl. tenant id, e.g. https://login.microsoftonline.com/<tenant> or https://<name>.ciamlogin.com/<tenant>. */
    authority: z.url(),
    /** Required for CIAM; the authority host, e.g. ["wesserextde.ciamlogin.com"]. */
    knownAuthorities: z.array(z.string().min(1)).default([]),
    /** Defaults to window.location.origin. */
    redirectUri: z.url().optional(),
    postLogoutRedirectUri: z.url().optional(),
    /**
     * Start the sign-in redirect as soon as an unauthenticated user arrives instead of
     * showing a button. On Entra-joined Windows devices Entra completes it silently
     * (device SSO), so internal staff never see a login form. Off for external hosts.
     */
    autoSignIn: z.boolean().default(false),
    /** Passed as domain_hint so Entra skips the account picker, e.g. "wesser.de". */
    domainHint: z.string().min(1).optional(),
  }),

  telemetry: z
    .object({
      appInsightsConnectionString: z.string().optional(),
      /** Mirror log output to the browser console (always on in dev builds). */
      consoleLogging: z.boolean().default(false),
    })
    .prefault({}),

  /** Simple on/off switches modules may consult via useFeature(). */
  features: z.record(z.string(), z.boolean()).prefault({}),
})

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>
